/**
 * Resources module exports
 */

import type { Resource, ResourceTemplate } from "@modelcontextprotocol/sdk/types.js";
import type { FlowManagementApi } from "../api/flow-management.js";
import type { ConnectorMetadataApi } from "../api/connector-metadata.js";
import type { SchemaCache } from "../cache/schema-cache.js";
import { hasFeature, type FeatureId } from "../config/features.js";

// Connector resources
export {
  connectorResourceTemplates,
  listConnectorResources,
  readConnectorResource,
  type ConnectorResourceContext,
} from "./connector-resource.js";

// Template resources
export {
  templateResourceTemplates,
  listTemplateResources,
  readTemplateResource,
  isTemplateResourceUri,
} from "./template-resource.js";

// Environment resources
export {
  environmentResourceTemplates,
  listEnvironmentResources,
  readEnvironmentResource,
  isEnvironmentResourceUri,
  type EnvironmentResourceContext,
} from "./environment-resource.js";

// ============================================================================
// Combined Resource Context
// ============================================================================

export interface ResourceContext {
  flowApi: FlowManagementApi;
  connectorApi: ConnectorMetadataApi | null;
  cache: SchemaCache | null;
  defaultEnvironmentId: string;
  enabledFeatures?: FeatureId[];
}

// ============================================================================
// Combined Resource Templates
// ============================================================================

import { connectorResourceTemplates } from "./connector-resource.js";
import { templateResourceTemplates } from "./template-resource.js";
import { environmentResourceTemplates } from "./environment-resource.js";

export function getAllResourceTemplates(features?: readonly FeatureId[]): ResourceTemplate[] {
  return [
    ...(hasFeature(features, "powerApps") ? connectorResourceTemplates : []),
    ...templateResourceTemplates,
    ...environmentResourceTemplates,
  ];
}

// ============================================================================
// Combined Resource Listing
// ============================================================================

import { listConnectorResources, type ConnectorResourceContext } from "./connector-resource.js";
import { listTemplateResources } from "./template-resource.js";
import { listEnvironmentResources, type EnvironmentResourceContext } from "./environment-resource.js";

export async function listAllResources(context: ResourceContext): Promise<Resource[]> {
  const resources: Resource[] = [];

  // Add template resources (no API call needed)
  resources.push(...listTemplateResources());

  // Add environment resources if API available
  if (context.flowApi) {
    const envContext: EnvironmentResourceContext = {
      api: context.flowApi,
      defaultEnvironmentId: context.defaultEnvironmentId,
    };
    const envResources = await listEnvironmentResources(envContext);
    resources.push(...envResources);
  }

  // Add connector resources if API available
  if (context.connectorApi) {
    const connectorContext: ConnectorResourceContext = {
      api: context.connectorApi,
      cache: context.cache,
    };
    const connectorResources = await listConnectorResources(connectorContext);
    resources.push(...connectorResources);
  }

  return resources;
}

// ============================================================================
// Combined Resource Reading
// ============================================================================

import { readConnectorResource } from "./connector-resource.js";
import { readTemplateResource, isTemplateResourceUri } from "./template-resource.js";
import { readEnvironmentResource, isEnvironmentResourceUri } from "./environment-resource.js";

export async function readResource(
  context: ResourceContext,
  uri: string
): Promise<{ contents: Array<{ uri: string; mimeType: string; text: string }> } | null> {
  // Template resources
  if (isTemplateResourceUri(uri)) {
    return readTemplateResource(uri);
  }

  // Environment resources
  if (isEnvironmentResourceUri(uri)) {
    if (!context.flowApi) return null;
    const envContext: EnvironmentResourceContext = {
      api: context.flowApi,
      defaultEnvironmentId: context.defaultEnvironmentId,
    };
    return readEnvironmentResource(envContext, uri);
  }

  // Connector resources (default)
  if (context.connectorApi) {
    const connectorContext: ConnectorResourceContext = {
      api: context.connectorApi,
      cache: context.cache,
    };
    return readConnectorResource(connectorContext, uri);
  }

  return null;
}
